import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { Store } from 'redux';
import { PersistGate } from 'redux-persist/integration/react';
import { SpotlightManager } from '@atlaskit/onboarding';
import AppBase from '@atlassian/jira-app-base';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales';
import { ViewExperienceTrackingProvider } from '@atlassian/jira-common-experience-tracking-viewing';
import { ReportErrors } from '@atlassian/jira-errors-handling';
import getMeta from '@atlassian/jira-get-meta';
import { TextField } from '@atlassian/jira-portfolio-3-common';
import { context } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics';
import { initialLoad } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/command';
import {
	setAnalyticsEvent,
	setBrowserHistory,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/command/app';
import { setInitialFilterValues } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/command/filters';
import { isPollinatorTenant } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/feature-flags';
import { persistor } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/store';
import type { State } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/types';
import AssociatedIssuesWrapper from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/associated-issues-provider';
import NowLine from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/timeline/now';
import { provideComponents } from '@atlassian/jira-portfolio-3-portfolio/src/common/component-slots';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import {
	ERROR_REPORTING_PACKAGE,
	ERROR_REPORTING_TEAM,
	PACKAGE_NAME,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { withViewExperienceTracker } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/experience-tracking';
import { CellSelectWithRollup } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/select';
import { getPathParams } from '@atlassian/jira-portfolio-3-portfolio/src/common/window';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MountEvent,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { connect } from '@atlassian/jira-react-redux';
import { JIRA_PORTFOLIO, type ApplicationEdition } from '@atlassian/jira-shared-types';
import { HistoryAdapter } from '@atlassian/jira-spa-router-adapters';
import UFOSegment from '@atlassian/jira-ufo-segment';
import AdvancedFieldsMenu from './component-slot-content/advanced-fields-menu';
import FieldDatePicker from './component-slot-content/field-date-picker';
import FieldMultiSelectCell from './component-slot-content/field-multi-select-cell';
import FieldPrioritySelect from './component-slot-content/field-priority-select';
import FieldSelect from './component-slot-content/field-select';
import FieldUserAvatarCell from './component-slot-content/field-user-avatar-cell';
import UserPicker from './component-slot-content/field-user-picker';
import FilterDependencyGraphFromRoadmap from './component-slot-content/filter-dependency-graph-from-roadmap';
import IssueSearchWarningClearFilter from './component-slot-content/issue-search-warning-clear-filter';
import QuickFilter from './component-slot-content/quick-filter';
import PortfolioErrorBoundary from './error-boundary';
import ErrorNotification from './error-notification';
import type { DispatchProps, OwnProps, ContextAnalyticsDataAttributes } from './types';
import View from './view';

const mapDispatchToProps = {
	initialLoad,
	setInitialFilterValues,
};

const Connected = withViewExperienceTracker(
	connect<{}, DispatchProps, OwnProps, State>(null, mapDispatchToProps)(View),
	'portfolio-3.simple-plans.app',
);

type Props = {
	locale: Locale;
	edition: ApplicationEdition;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	store: Store<any>;
};

const App = ({ locale, edition, store }: Props) => {
	useEffect(() => {
		/**
		 * We are adding this special class to 'jira-frontend' div to scope the react virtualized
		 * classes defined in src/app-simple-plans/styles.css. CSS styles for these classes can be found
		 * in many places and we are scoping them all.
		 * When we are in Jira SPA, we can not pollute the global classes and hence we are
		 * scoping them to ARJ application.
		 * We remove the special .arj class once our app is unmounted. Look into the returned function below.
		 */

		const JfeRootElement = document.getElementById('jira-frontend');
		if (JfeRootElement) {
			JfeRootElement.classList.add('arj');
		}

		return function removeArjClass() {
			// Remove special .arj class from 'jira-frontend' div.
			// eslint-disable-next-line @typescript-eslint/no-shadow
			const JfeRootElement = document.getElementById('jira-frontend');
			if (JfeRootElement) {
				JfeRootElement.classList.remove('arj');
			}
		};
	}, []);

	const { id: planId, sid: scenarioId } = getPathParams();
	/**
	 * BEGIN: searchSessionId
	 * When the plan is accessed through the Jira search bar, multiple search parameters are added to the
	 * query including the searchSessionId.
	 * In "src/packages/session-tracker/src/view/index.js#109", these parameters are then striped from the
	 * query params and added to the metadata on the history change. The previous session's metadata is also
	 * cleaned on the history change.
	 * During the SPA, transition the app can get rendered multiple times because of the prop changes in the
	 * SPA parent components. Because of this, the searchSessionId is available during the first render and
	 * gets cleaned after that.
	 * We use searchSessionId to track user's activity. To retain this value, we are using the "useMemo" hook
	 * with the "planId". So until the planId changes, it will not re-read the value and keeps the first value
	 * it read.
	 */
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const searchSessionId = useMemo(() => getMeta('ajs-search-session-id'), [planId]);
	// END: searchSessionId

	let contextAnalyticsDataAttributes: ContextAnalyticsDataAttributes = {
		planId,
		scenarioId,
		context: context.PLAN,
	};

	if (isDefined(searchSessionId)) {
		contextAnalyticsDataAttributes = {
			...contextAnalyticsDataAttributes,
			searchSessionId,
		};
	}

	return (
		<UFOSegment name="view-advanced-roadmaps">
			<ViewExperienceTrackingProvider
				experience="viewPortfolio3"
				analyticsSource="portfolio3"
				application={JIRA_PORTFOLIO}
				edition={edition}
				additionalAttributes={{
					synthetic: isPollinatorTenant(),
				}}
			>
				<AppBase store={store} hasErrorBoundary={false}>
					<AssociatedIssuesWrapper>
						<ContextualAnalyticsData
							sourceType={SCREEN}
							sourceName="portfolio3"
							attributes={contextAnalyticsDataAttributes}
						>
							<MountEvent
								onMount={(analyticsEvent) => store.dispatch(setAnalyticsEvent(analyticsEvent))}
							/>
							<HistoryAdapter>
								{/* @ts-expect-error - TS2322 - Type '(history: any) => null' is not assignable to type '(history: any) => ReactElement<any, string | JSXElementConstructor<any>>'. */}
								{(history) => {
									store.dispatch(setBrowserHistory(history));
									return null;
								}}
							</HistoryAdapter>
							<FireScreenAnalytics />
							{/**
							 PortfolioErrorBoundary is portfolio specific error boundary and is used to
							 show fallback UI when React tree is broken and also show error flags for
							 other errors.
							 */}
							<PortfolioErrorBoundary>
								{/**
								 ReportErrors is an error boundary provided by JFP. It reports errors to
								 SignalFX and Sentry. It report error messages and error stacktraces only to
								 Sentry, but not to SignalFX. SignalFX only gets error count.
								 This component also rethrows the error, so that we can show fallback UI.
								 */}
								<ReportErrors
									id={ERROR_REPORTING_PACKAGE.APP}
									packageName={PACKAGE_NAME}
									teamName={ERROR_REPORTING_TEAM}
									sendToPrivacyUnsafeSplunk
								>
									{document.body && createPortal(<ErrorNotification />, document.body)}
									<PersistGate loading={null} persistor={persistor}>
										<SpotlightManager>
											<Connected locale={locale} planId={planId} scenarioId={scenarioId} />
										</SpotlightManager>
									</PersistGate>
								</ReportErrors>
							</PortfolioErrorBoundary>
						</ContextualAnalyticsData>
					</AssociatedIssuesWrapper>
				</AppBase>
			</ViewExperienceTrackingProvider>
		</UFOSegment>
	);
};

export default provideComponents<Props>({
	FieldSelect,
	FieldPrioritySelect,
	FieldMultiSelectCell,
	FieldDatePicker,
	CellSelectWithRollup,
	CellTextField: TextField,
	AdvancedFieldsMenu,
	FieldUserAvatarCell,
	FilterDependencyGraphFromRoadmap,
	QuickFilter,
	IssueSearchWarningClearFilter,
	NowLine,
	UserPicker,
})(App);
